/**
 * Comparison logic for Raketech's old vs new reconciliation.
 *
 * Kept out of the page component so the matching rules and the margin
 * assumption are readable in one place, and so the page only renders.
 *
 * Two facts drive everything here:
 *
 *  1. The old reconciliation's `total_new_earnings` is already net of
 *     Raketech's ~5% share, while the new workbook's monthly columns are gross
 *     — that is why the workbook multiplies partner accounts by 0.95 to reach
 *     its "Adwin earnings 1st period". Comparing the two without putting them
 *     on the same basis overstates the new figures by 5% on every partner
 *     account, so `applyMargin` (default on) does that normalisation.
 *
 *  2. The old recon's `account_id` is the same identifier as the workbook's
 *     `HUB ID`, verified against IVY/Lumi (recon 14271 = HUB 14271 / AC 16110).
 *     That makes account-level joins exact; offer codes then split accounts
 *     into their individual deals.
 */

import type {
  AccountDiffRow,
  BridgeStep,
  CoverageBucket,
  NewReconAccount,
  NewReconDataset,
  NewReconOffer,
  OfferDiffMonth,
  OfferPair,
  OldReconOffer,
  ReconDiffData,
  ReconDiffSummary,
} from '../types/raketechReconDiff.types';

/** Offer codes are hand-typed on both sides; the workbook has entries such as
 *  'RT_ CA_SP_A23_FI_AID10202' with a stray space. */
export function normalizeOfferCode(offer: string): string {
  return (offer || '').replace(/\s+/g, '').toUpperCase();
}

/** Placeholders the workbook uses where a real offer code is unknown. These are
 *  reused across many accounts, so they can never identify an offer alone. */
const PLACEHOLDER_OFFERS = new Set([
  '', 'DEFAULT', 'DEFAULTCAMPAIGN', 'UNABLETOCHECK', 'FBLINK', 'WRLINK', 'N/A', '-',
]);

function isPlaceholderOffer(offer: string): boolean {
  return PLACEHOLDER_OFFERS.has(normalizeOfferCode(offer));
}

const r2 = (v: number) => Math.round(v * 100) / 100;

function sumMonths(monthly: Record<string, number>, months: string[]): number {
  let total = 0;
  for (const month of months) total += monthly[month] || 0;
  return total;
}

export interface DiffOptions {
  /** Put the new gross figures on the old figures' net-of-margin basis. */
  applyMargin: boolean;
}

export interface DiffResult {
  rows: AccountDiffRow[];
  summary: ReconDiffSummary;
  coverage: CoverageBucket[];
  accounts: NewReconAccount[];
}

/** Expand the columnar account table into objects. */
export function expandAccounts(dataset: NewReconDataset): NewReconAccount[] {
  const { fields, rows } = dataset.accounts;
  return rows.map((row) => {
    const record: Record<string, string> = {};
    fields.forEach((field, index) => { record[field] = row[index] ?? ''; });
    return record as unknown as NewReconAccount;
  });
}

/** New-side earnings for one month, on the requested margin basis. */
function newMonthValue(offer: NewReconOffer, month: string, dataset: NewReconDataset, applyMargin: boolean): number {
  const gross = offer.monthly[month] || 0;
  if (!applyMargin || !offer.pa) return gross;
  // Raketech stopped taking the margin on partner accounts after the first
  // period, and reports those months gross in their own Summary sheet.
  if (month > dataset.period1End) return gross;
  return gross * (1 - dataset.marginRate);
}

function newRangeValue(
  offer: NewReconOffer, months: string[], dataset: NewReconDataset, applyMargin: boolean,
): number {
  let total = 0;
  for (const month of months) total += newMonthValue(offer, month, dataset, applyMargin);
  return total;
}

function buildMonthlyDiff(
  oldMonthly: Record<string, number>,
  newOffers: NewReconOffer[],
  months: string[],
  dataset: NewReconDataset,
  applyMargin: boolean,
): OfferDiffMonth[] {
  const out: OfferDiffMonth[] = [];
  for (const month of months) {
    const oldValue = r2(oldMonthly[month] || 0);
    const newValue = r2(newOffers.reduce((s, o) => s + newMonthValue(o, month, dataset, applyMargin), 0));
    if (oldValue === 0 && newValue === 0) continue;
    out.push({ month, old: oldValue, new: newValue, delta: r2(newValue - oldValue) });
  }
  return out;
}

/** Build the per-offer breakdown shown inside an account row. */
function buildOfferPairs(
  newOffers: NewReconOffer[],
  oldOffers: OldReconOffer[],
  overlapMonths: string[],
  tailMonths: string[],
  dataset: NewReconDataset,
  applyMargin: boolean,
): OfferPair[] {
  const pairs = new Map<string, OfferPair>();

  const keyFor = (offer: string, brand: string) =>
    (isPlaceholderOffer(offer) ? `brand:${brand.toLowerCase()}` : normalizeOfferCode(offer));

  for (const offer of newOffers) {
    const key = keyFor(offer.offer, offer.brand);
    const existing = pairs.get(key);
    const value = r2(newRangeValue(offer, overlapMonths, dataset, applyMargin));
    const tail = r2(newRangeValue(offer, tailMonths, dataset, applyMargin));
    if (existing) {
      existing.new = r2(existing.new + value);
      existing.newTail = r2(existing.newTail + tail);
      existing.pa = existing.pa || offer.pa;
    } else {
      pairs.set(key, {
        offer: offer.offer || `(${offer.brand})`,
        brand: offer.brand,
        old: 0, new: value, delta: value,
        inOld: false, inNew: true, pa: offer.pa, newTail: tail,
      });
    }
  }

  for (const offer of oldOffers) {
    const key = keyFor(offer.offer, offer.brand);
    const value = r2(sumMonths(offer.monthly, overlapMonths));
    const existing = pairs.get(key);
    if (existing) {
      existing.old = r2(existing.old + value);
      existing.inOld = true;
    } else {
      pairs.set(key, {
        offer: offer.offer || `(${offer.brand})`,
        brand: offer.brand,
        old: value, new: 0, delta: -value,
        inOld: true, inNew: false, pa: false, newTail: 0,
      });
    }
  }

  return [...pairs.values()]
    .map((pair) => ({ ...pair, delta: r2(pair.new - pair.old) }))
    .sort((a, b) => Math.max(b.new, b.old) - Math.max(a.new, a.old));
}

/**
 * Join the two reconciliations at account level.
 *
 * Account ids are the only identifier stable across both reports, so every
 * offer on each side is folded into its account and the two account totals are
 * compared. Offers are still reconciled inside each account (see
 * `buildOfferPairs`) but they never determine whether an account matched, which
 * is what keeps a reorganised offer from looking like a deletion.
 */
export function buildDiff(
  dataset: NewReconDataset,
  old: ReconDiffData,
  options: DiffOptions,
): DiffResult {
  const { applyMargin } = options;

  const overlapMonths = dataset.months.filter(
    (m) => m >= old.reconPeriod.start && m <= old.reconPeriod.end,
  );
  const tailMonths = dataset.months.filter((m) => m > old.reconPeriod.end);
  const knownAcIds = new Set(old.knownAcIds);

  const newByAccount = new Map<string, NewReconOffer[]>();
  for (const offer of dataset.offers) {
    const key = offer.hubId || `ac:${offer.acId}`;
    if (!newByAccount.has(key)) newByAccount.set(key, []);
    newByAccount.get(key)!.push(offer);
  }

  const oldByAccount = new Map<string, OldReconOffer[]>();
  for (const offer of old.oldOffers) {
    const key = offer.accountId;
    if (!oldByAccount.has(key)) oldByAccount.set(key, []);
    oldByAccount.get(key)!.push(offer);
  }

  const rows: AccountDiffRow[] = [];
  const accountKeys = new Set([...newByAccount.keys(), ...oldByAccount.keys()]);

  for (const accountKey of accountKeys) {
    const newOffers = newByAccount.get(accountKey) || [];
    const oldOffers = oldByAccount.get(accountKey) || [];
    const matchMethod: AccountDiffRow['matchMethod'] =
      newOffers.length && oldOffers.length ? 'both' : (newOffers.length ? 'new_only' : 'old_only');

    const oldMonthly: Record<string, number> = {};
    for (const offer of oldOffers) {
      for (const [month, value] of Object.entries(offer.monthly)) {
        oldMonthly[month] = (oldMonthly[month] || 0) + value;
      }
    }

    const oldOverlap = r2(sumMonths(oldMonthly, overlapMonths));
    const newOverlap = r2(newOffers.reduce((s, o) => s + newRangeValue(o, overlapMonths, dataset, applyMargin), 0));
    const newOverlapGross = r2(newOffers.reduce((s, o) => s + sumMonths(o.monthly, overlapMonths), 0));
    const monthly = buildMonthlyDiff(oldMonthly, newOffers, overlapMonths, dataset, applyMargin);
    const offers = buildOfferPairs(newOffers, oldOffers, overlapMonths, tailMonths, dataset, applyMargin);

    const acIds = [...new Set(newOffers.map((o) => o.acId).filter(Boolean))];
    const syncedTail = acIds.reduce((s, acId) => {
      const synced = old.syncedByAcId[acId];
      return s + (synced ? sumMonths(synced.monthly, tailMonths) : 0);
    }, 0);

    rows.push({
      key: accountKey,
      matchMethod,

      hubId: newOffers[0]?.hubId || oldOffers[0]?.accountId || accountKey,
      acIds,
      partner: newOffers[0]?.partner || oldOffers[0]?.partner || '',
      brands: [...new Set([...newOffers.map((o) => o.brand), ...oldOffers.map((o) => o.brand)].filter(Boolean))],
      geos: [...new Set(oldOffers.map((o) => o.geo).filter(Boolean))],
      status: newOffers[0]?.status || '',
      system: newOffers[0]?.system || '',
      pa: newOffers.some((o) => o.pa),

      oldOverlap,
      newOverlap,
      newOverlapGross,
      delta: r2(newOverlap - oldOverlap),
      deltaPct: oldOverlap !== 0 ? r2(((newOverlap - oldOverlap) / Math.abs(oldOverlap)) * 100) : null,

      monthly,
      monthsOnlyOld: monthly.filter((m) => m.old !== 0 && m.new === 0).length,
      monthsOnlyNew: monthly.filter((m) => m.new !== 0 && m.old === 0).length,

      offers,
      reorganised: matchMethod === 'both' && offers.some((o) => !o.inOld || !o.inNew),

      newTail: r2(newOffers.reduce((s, o) => s + newRangeValue(o, tailMonths, dataset, applyMargin), 0)),
      syncedTail: r2(syncedTail),

      oldCurrency: oldOffers[0]?.currency || '',
      oldFtds: oldOffers.reduce((s, o) => s + o.ftds, 0),
      existsInAdwstats: acIds.some((acId) => knownAcIds.has(acId)),
    });
  }

  rows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  // -------------------------------------------------------------------------
  // Summary
  // -------------------------------------------------------------------------
  const newOnly = rows.filter((r) => r.matchMethod === 'new_only');
  const oldOnly = rows.filter((r) => r.matchMethod === 'old_only');

  const oldOverlapTotal = r2(rows.reduce((s, r) => s + r.oldOverlap, 0));
  const newOverlapTotal = r2(rows.reduce((s, r) => s + r.newOverlap, 0));
  const missingRows = rows.filter((r) => r.acIds.length > 0 && !r.existsInAdwstats);
  const allOffers = rows.flatMap((r) => r.offers);

  const summary: ReconDiffSummary = {
    overlapMonths,
    tailMonths,

    matchedAccounts: rows.filter((r) => r.matchMethod === 'both').length,
    newOnlyAccounts: newOnly.length,
    oldOnlyAccounts: oldOnly.length,
    reorganisedAccounts: rows.filter((r) => r.reorganised).length,

    matchedOffers: allOffers.filter((o) => o.inOld && o.inNew).length,
    newOnlyOffers: allOffers.filter((o) => o.inNew && !o.inOld).length,
    oldOnlyOffers: allOffers.filter((o) => o.inOld && !o.inNew).length,

    oldOverlapTotal,
    newOverlapTotal,
    newOverlapGrossTotal: r2(rows.reduce((s, r) => s + r.newOverlapGross, 0)),
    overlapDelta: r2(newOverlapTotal - oldOverlapTotal),
    overlapDeltaPct: oldOverlapTotal !== 0
      ? r2(((newOverlapTotal - oldOverlapTotal) / Math.abs(oldOverlapTotal)) * 100)
      : null,

    newTailTotal: r2(rows.reduce((s, r) => s + r.newTail, 0)),
    syncedTailTotal: r2(rows.reduce((s, r) => s + r.syncedTail, 0)),

    newOnlyEarnings: r2(newOnly.reduce((s, r) => s + r.newOverlap + r.newTail, 0)),
    oldOnlyEarnings: r2(oldOnly.reduce((s, r) => s + r.oldOverlap, 0)),

    accountsMissingFromAdwstats: missingRows.length,
    earningsMissingFromAdwstats: r2(missingRows.reduce((s, r) => s + r.newOverlap + r.newTail, 0)),
  };

  // -------------------------------------------------------------------------
  // Coverage by partner
  // -------------------------------------------------------------------------
  const buckets = new Map<string, CoverageBucket>();
  for (const row of rows) {
    const partner = row.partner || 'Unknown';
    if (!buckets.has(partner)) {
      buckets.set(partner, {
        partner,
        newAccounts: 0, oldAccounts: 0,
        newEarnings: 0, oldEarnings: 0, delta: 0,
        unmatchedNew: 0, unmatchedOld: 0, reorganised: 0,
        missingFromAdwstats: 0,
      });
    }
    const bucket = buckets.get(partner)!;
    if (row.matchMethod !== 'old_only') bucket.newAccounts += 1;
    if (row.matchMethod !== 'new_only') bucket.oldAccounts += 1;
    bucket.newEarnings += row.newOverlap + row.newTail;
    bucket.oldEarnings += row.oldOverlap;
    bucket.delta += row.delta;
    if (row.matchMethod === 'new_only') bucket.unmatchedNew += 1;
    if (row.matchMethod === 'old_only') bucket.unmatchedOld += 1;
    if (row.reorganised) bucket.reorganised += 1;
    if (row.acIds.length > 0 && !row.existsInAdwstats) bucket.missingFromAdwstats += 1;
  }
  const coverage = [...buckets.values()]
    .map((b) => ({
      ...b,
      newEarnings: r2(b.newEarnings),
      oldEarnings: r2(b.oldEarnings),
      delta: r2(b.delta),
    }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  return { rows, summary, coverage, accounts: expandAccounts(dataset) };
}

// ---------------------------------------------------------------------------
// Their bottom line, step by step
// ---------------------------------------------------------------------------

/**
 * Transaction fees implied by Raketech's own Finance ledger.
 *
 * Settlement rows carry both `Amount` and `Excluding Fee`; the gap between them
 * is the fee withheld on that payment. Summing those gaps gives an independent
 * figure to hold against the two fee lines they state on the Summary sheet.
 */
export function feesFromLedger(dataset: NewReconDataset): {
  total: number;
  byMonth: { month: string; amount: number }[];
  reconPeriod: number;
  postReconPeriod: number;
  afterApril2025: number;
  rows: number;
} {
  const byMonth = new Map<string, number>();
  let total = 0;
  let rows = 0;

  for (const entry of dataset.finance.entries) {
    if (entry.documentType.toLowerCase() === 'invoice') continue;
    const fee = (entry.amount || 0) - (entry.excludingFee || 0);
    if (Math.abs(fee) < 0.005) continue;
    total += fee;
    rows += 1;
    const month = (entry.postingDate || 'undated').slice(0, 7);
    byMonth.set(month, (byMonth.get(month) || 0) + fee);
  }

  const sorted = [...byMonth.entries()]
    .map(([month, amount]) => ({ month, amount: r2(amount) }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const inRange = (month: string, start: string, end: string) => month >= start && month <= end;

  return {
    total: r2(total),
    byMonth: sorted,
    reconPeriod: r2(sorted.filter((f) => inRange(f.month, '2023-03', '2024-11')).reduce((s, f) => s + f.amount, 0)),
    postReconPeriod: r2(sorted.filter((f) => inRange(f.month, '2024-12', '2025-04')).reduce((s, f) => s + f.amount, 0)),
    afterApril2025: r2(sorted.filter((f) => f.month > '2025-04').reduce((s, f) => s + f.amount, 0)),
  rows,
  };
}

/** Raketech's bridge from total earnings down to what they admit owing. */
export function buildTheirBridge(dataset: NewReconDataset): BridgeStep[] {
  const c = dataset.controls;
  const ledgerFees = feesFromLedger(dataset);
  const steps: BridgeStep[] = [];

  steps.push({
    label: 'Total Adwin earnings, Mar 2023 – May 2026',
    amount: c.statedTotalEarnings,
    kind: 'figure',
    note: `Their headline. Period 1 ${c.statedPeriod1.toLocaleString('en-US', { maximumFractionDigits: 0 })} + period 2 ${c.statedPeriod2.toLocaleString('en-US', { maximumFractionDigits: 0 })}.`,
  });

  if (Math.abs(c.headlineVsPerOfferGap) > 1) {
    steps.push({
      label: 'Same figure summed from their own per-offer column',
      amount: c.sumAdwinEarnings,
      kind: 'gap',
      derived: true,
      discrepancy: c.headlineVsPerOfferGap,
      note: 'Their headline adds period 2 gross, but their per-offer "Adwin earnings" column applies the 5% partner margin to it. The headline is the higher of the two, so correcting it would reduce the amount they owe.',
    });
  }

  steps.push({
    label: 'Less: total paid (settlements in their Finance ledger)',
    amount: c.totalPaid !== null ? -c.totalPaid : null,
    kind: 'deduction',
    note: `${dataset.finance.entries.filter((e) => e.documentType.toLowerCase() !== 'invoice').length} settlement rows. Their invoices total ${(c.totalInvoiced ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}, leaving ${r2((c.totalPaid ?? 0) + (c.totalInvoiced ?? 0)).toLocaleString('en-US', { maximumFractionDigits: 0 })} invoiced but unsettled.`,
  });

  steps.push({
    label: 'Difference',
    amount: c.difference,
    kind: 'subtotal',
    note: 'Earnings they acknowledge, less what they have paid.',
  });

  steps.push({
    label: 'Less: transaction fees they charge to Adwin',
    amount: -c.transactionFees,
    kind: 'deduction',
    derived: false,
    discrepancy: r2(ledgerFees.total - c.transactionFees),
    note: `Stated as ${Object.entries(c.transactionFeeLines).map(([k, v]) => `${k.replace('Transaction fees ', '')} ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`).join(' + ')}. Their ledger shows ${ledgerFees.total.toLocaleString('en-US', { maximumFractionDigits: 0 })} of fees across ${ledgerFees.rows} settlements, and attributes them to different periods: ${ledgerFees.reconPeriod.toLocaleString('en-US', { maximumFractionDigits: 0 })} in Mar 23 – Nov 24 against the ${(c.transactionFeeLines['Transaction fees Mar 23 - Nov 24'] ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} claimed.`,
  });

  steps.push({
    label: 'Underpayment to Adwin, per Raketech',
    amount: c.underpaymentAfterFees,
    kind: 'result',
    note: 'The figure their workbook concludes with.',
  });

  return steps;
}
